import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { loadingStore } from 'mobx-loading'
import { RECRUITER_TOKENS as T } from '~/themes/recruiter-tokens'

// How long a request may run before the page admits it is waiting. Most recruiter queries return
// well inside this, and a signal that flashes for 150ms reads as flicker, not feedback.
export const INDICATOR_DELAY_MS = 600

const SKELETON_SELECTOR = '.MuiSkeleton-root'

const spinnerKeyframes = `@keyframes opus-loading-spin { to { transform: rotate(360deg); } }
@keyframes opus-loading-in { from { opacity: 0; transform: translate(-50%, -6px); } to { opacity: 1; transform: translate(-50%, 0); } }`

const styles = {
	pill: {
		position: 'fixed',
		top: 10,
		left: '50%',
		transform: 'translate(-50%, 0)',
		zIndex: 1400,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 8,
		height: 30,
		padding: '0 14px 0 10px',
		borderRadius: 999,
		border: `1px solid ${T.border}`,
		background: T.surface,
		boxShadow: '0 4px 14px rgba(16, 42, 51, 0.12)',
		fontFamily: 'Avenir, sans-serif',
		fontSize: T.font.md,
		fontWeight: 600,
		color: T.inkMuted,
		animation: 'opus-loading-in 160ms ease-out',
		pointerEvents: 'none',
	},
	spinner: {
		width: 14,
		height: 14,
		borderRadius: '50%',
		border: `2px solid ${T.accentSoft}`,
		borderTopColor: T.accent,
		animation: 'opus-loading-spin 700ms linear infinite',
	},
}

/**
 * Replaces the full-width bar that ran across the top of the window on every request.
 *
 * Lists and detail pages now show skeletons where their content will land, so this only appears
 * when nothing on screen is already saying "loading": a request has run past INDICATOR_DELAY_MS
 * and there is no skeleton in the document. It is a small status pill, not a progress bar — the
 * app cannot know how far along a GraphQL request is, and a bar that pretends to is the part that
 * looked unfinished.
 */
export const GlobalLoadingIndicator = observer(() => {
	const active = Boolean(loadingStore.global)
	const [visible, setVisible] = useState(false)

	useEffect(() => {
		if (!active) {
			setVisible(false)
			return undefined
		}

		let interval
		const check = () => setVisible(!document.querySelector(SKELETON_SELECTOR))
		const timer = setTimeout(() => {
			check()
			interval = setInterval(check, 300)
		}, INDICATOR_DELAY_MS)

		return () => {
			clearTimeout(timer)
			clearInterval(interval)
		}
	}, [active])

	if (!visible) return null

	return (
		<>
			<style>{spinnerKeyframes}</style>
			<div role="status" aria-live="polite" style={styles.pill}>
				<span style={styles.spinner} aria-hidden="true" />
				Loading…
			</div>
		</>
	)
})

/** The first paint, before the theme and the route are ready: a quiet centred spinner on the page ground. */
export const PageLoader = () => (
	<>
		<style>{spinnerKeyframes}</style>
		<div
			role="status"
			aria-live="polite"
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 12,
				minHeight: '100vh',
				background: T.surfaceSubtle,
			}}
		>
			<span style={{ ...styles.spinner, width: 28, height: 28, borderWidth: 3 }} aria-hidden="true" />
			<span style={{ fontFamily: 'Avenir, sans-serif', fontSize: T.font.lg, color: T.inkMuted }}>Loading…</span>
		</div>
	</>
)
